import { Link } from 'react-router-dom';
import { ShieldCheck } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import type { RateSet, ReportThresholds } from '../types/analytics-api.types';
import { fmtPct } from '../utils/report-format';

type SenderHealthCardProps = {
  rates: RateSet;
  thresholds: ReportThresholds;
  days: number;
  deliveryDataAvailable: boolean;
};

export function SenderHealthCard({
  rates,
  thresholds,
  days,
  deliveryDataAvailable,
}: SenderHealthCardProps) {
  const rows = [
    {
      label: 'Delivery rate',
      value: deliveryDataAvailable ? rates.deliveryRate : null,
      limit: null as number | null,
      digits: 1,
    },
    {
      label: 'Bounce rate',
      value: rates.bounceRate,
      limit: thresholds.bounceRate,
      digits: 1,
    },
    {
      label: 'Complaint rate',
      value: rates.complaintRate,
      limit: thresholds.complaintRate,
      digits: 2,
    },
  ];

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-neutral-900">Sender health</h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            Across every send in range — not campaign performance, but whether mailbox providers
            still trust us.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="gap-2 text-xs">
          <Link to={`/reports/deliverability?days=${days}`}>
            <ShieldCheck className="size-[15px]" aria-hidden />
            Deliverability report
          </Link>
        </Button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {rows.map((x) => {
          const over = !!(x.limit != null && x.value != null && x.value >= x.limit);
          return (
            <div key={x.label}>
              <div className="text-xs text-neutral-500">{x.label}</div>
              <div
                className={`mt-0.5 text-lg font-semibold ${over ? 'text-[#B3261E]' : 'text-neutral-900'}`}
              >
                {fmtPct(x.value, x.digits)}
              </div>
              {x.limit != null ? (
                <div className="text-xs text-neutral-500">
                  limit {fmtPct(x.limit, x.limit < 0.01 ? 2 : 1)}
                </div>
              ) : (
                <div className="text-xs text-neutral-500">
                  {deliveryDataAvailable ? 'no ceiling' : 'delivery data not received yet'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
